import { useNavigation } from "@repo/core/hooks/useNavigation";
import { useRouteParams } from "@repo/core/hooks/useRouteParams";
import { ROUTES } from "@repo/core/routes";
import { useQueryClient } from "@tanstack/react-query";
import {
  normalizeProposalIdParam,
  PROPOSAL_VIEW_INFO_QUERY_ROOTS
} from "@repo/core/hooks/useRefetchProposalViewInfoQueries/invalidateProposalViewInfoQueries";
import { parseRouteFlag } from "../utils/proposalFlowNavigation";
import { useRef, useState } from "react";

type SuccessAction = "finish" | "limits";

export const ProposalEffectiveSuccessController = () => {
  const { replace } = useNavigation();
  const { proposalId, isCreditProposal: isCreditProposalParam } =
    useRouteParams();
  const isCreditProposal = parseRouteFlag(isCreditProposalParam);
  const normalizedProposalId = normalizeProposalIdParam(proposalId);

  const queryClient = useQueryClient();
  const actionInProgressRef = useRef<SuccessAction | null>(null);
  const [actionInProgress, setActionInProgress] =
    useState<SuccessAction | null>(null);

  const runAction = async (
    action: SuccessAction,
    callback: () => Promise<void>
  ) => {
    if (actionInProgressRef.current) {
      return;
    }

    actionInProgressRef.current = action;
    setActionInProgress(action);

    try {
      await callback();
    } finally {
      actionInProgressRef.current = null;
      setActionInProgress(null);
    }
  };

  const handleFinish = async () => {
    await runAction("finish", async () => {
      await queryClient.invalidateQueries({ queryKey: ["proposalsSearch"] });
      replace(ROUTES.proposals);
    });
  };

  const handleGoToSeeLimits = async () => {
    await runAction("limits", async () => {
      if (normalizedProposalId) {
        await Promise.all(
          PROPOSAL_VIEW_INFO_QUERY_ROOTS.map((queryRoot) =>
            queryClient.invalidateQueries({
              queryKey: [queryRoot, normalizedProposalId]
            })
          )
        );
      }

      replace(ROUTES.proposalsViewInfo, {
        proposalId: normalizedProposalId
      });
    });
  };

  return {
    handleFinish,
    handleGoToSeeLimits,
    isCreditProposal,
    actionInProgress
  };
};
